import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DraftsIcon from "@mui/icons-material/Drafts";
import AddItemForm from "../Inventory/ItemForm";
import {
  SalesOrderCreate,
  SalesOrderUpdate,
  getDivisionUnits,
  SalesOrderDetail,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
  ApprovePartyList,
  GetNextSoNo,
  getAllQuotations,
} from "../../../Services/Services";
import { useCallback } from "react";
import { debounce } from "lodash";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function SalesOrderEntry() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [divisionList, setDivision] = useState([]);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyPage, setPartyPage] = useState(1);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partySearch, setPartySearch] = useState("");
  const [units, setUnits] = useState([]);
  const [file, setFile] = useState(null);
  const [openItemModal, setOpenItemModal] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [quotationOptions, setQuotationOptions] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [existingFileName, setExistingFileName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState({
    division: storedDivision?.division_id || "",
    division_name: "",
    party: "",
    project_name: "",
    order_date: "",
    so_no: "",
    project: "",
    po_number: "",
    created_by: localStorage.getItem("user_id"),
    enquiry: "",
    quotation: "",
    quotation_no: "",
    cgst_per: 0,
    sgst_per: 0,
    round_off: 0,
    grand_total: 0,
    total_amount: 0,
    terms_and_conditions: [
      { key: "Delivery Period", value: "Within 15 days", is_default: true },
      { key: "Warranty", value: "12 months from delivery", is_default: true },
      { key: "Freight", value: "Included in price", is_default: true },
      { key: "Inspection", value: "By buyer at site", is_default: true },
      { key: "Quality", value: "As per specifications", is_default: true },
      { key: "Rejection/Claim", value: "Allowed within 7 days", is_default: true },
      { key: "Jurisdiction", value: "Durg", is_default: true },
      { key: "Payment Terms", value: "50% advance, balance on delivery", is_default: true },
      { key: "TCS/TDS", value: "As applicable", is_default: true },
      { key: "Remark", value: "None", is_default: true },
    ],
    items: [
      {
        category: "",
        item: "",
        description: "",
        quantity: 0,
        unit: "",
        rate: 0,
        amount: 0,
        dis_per: 0,
      },
    ],
  });

  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const location = useLocation();
  const orderData = location.state?.data || null;
  const amd = location.state?.amd;

  // ── Initial data fetch ──────────────────────────────────────────
  useEffect(() => {
    fetchDivionUnits();
    getCategories()
      .then((res) => setCategories(Array.isArray(res) ? res : res?.results || []))
      .catch((err) => console.error("Error fetching categories:", err));

    getAllQuotations()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.results || [];
        setQuotationOptions(list);
      })
      .catch((err) => console.error("Error fetching quotations:", err));
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getParties(1, { name: partySearch });
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [partySearch]);

  useEffect(() => {
    if (!isEditMode) fetchNextSoNo();
  }, [isEditMode]);

  const fetchDivionUnits = async () => {
    try {
      const data = await getDivisionUnits();
      setUnits(data?.units || []);
      setDivision(data?.divisions || []);
    } catch (err) {
      console.error("fetchDivionUnits error:", err);
    }
  };

  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];
      if (pageNum === 1) setPartyOptions(results);
      else setPartyOptions((prev) => [...prev, ...results]);
      setPartyPage(pageNum);
    } catch (err) {
      toast.error("Failed to load parties");
    } finally {
      setPartyLoading(false);
    }
  };

  const fetchNextSoNo = async () => {
    try {
      const data = await GetNextSoNo();
      if (data?.so_no) {
        setFormData((prev) => ({ ...prev, so_no: data.so_no }));
      }
    } catch (err) {
      console.error("fetchNextSoNo error:", err);
    }
  };

  const handleQuotationSelect = (event, newValue) => {
    setSelectedQuotation(newValue);
    if (!newValue) {
      setFormData((prev) => ({
        ...prev,
        quotation: "",
        quotation_no: "",
        items: [
          { category: "", item: "", description: "", quantity: 0, unit: "", rate: 0, amount: 0, dis_per: 0 },
        ],
        total_amount: 0,
        cgst_amt: 0,
        sgst_amt: 0,
        grand_total: 0,
      }));
      return;
    }

    const srcItems = newValue.items_list || newValue.items || [];
    const mapped = srcItems.length > 0
      ? srcItems.map((it) => ({
        category: it.category
          ? { id: it.category, name: it.category_name || "" }
          : "",
        item: it.item
          ? { id: it.item, name: it.item_name || "", unit: it.unit || "", rate: it.rate || 0 }
          : "",
        description: it.description || it.item_name || "",
        quantity: parseFloat(it.quantity) || 0,
        unit: it.unit || it.unit_name || "",
        rate: parseFloat(it.rate) || 0,
        amount: parseFloat(it.amount) || (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0),
        dis_per: parseFloat(it.dis_per) || 0,
      }))
      : [{ category: "", item: "", description: "", quantity: 0, unit: "", rate: 0, amount: 0, dis_per: 0 }];

    mapped.forEach(async (row, idx) => {
      if (row.category?.id) {
        try {
          const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, 0, 1, "");
          const list = (itemData?.results || []).map((i) => ({ ...i, id: i.id, name: i.name }));
          setItems((prev) => ({ ...prev, [idx]: list }));
        } catch { }
      }
    });

    const total = mapped.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const cgst_amt = (total * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total * (formData.sgst_per || 0)) / 100;
    const grand = total + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      quotation: newValue.id || newValue.quotation_id,
      quotation_no: newValue.ref_no || "",
      project_name: prev.project_name || newValue.subject || "",
      party: prev.party || (newValue.to_company_id || ""),
      items: mapped,
      total_amount: Number(total.toFixed(2)),
      cgst_amt: Number(cgst_amt.toFixed(2)),
      sgst_amt: Number(sgst_amt.toFixed(2)),
      grand_total: Number(grand.toFixed(2)),
    }));

    toast.success(`Quotation ${newValue.ref_no} linked — ${mapped.length} items loaded`);
  };

  const initialFormData = {
    division: "",
    division_name: "",
    party: "",
    project_name: "",
    order_date: "",
    project: "",
    terms_and_conditions: [],
    items: [{ category: "", item: "", description: "", quantity: 0, unit: "", rate: 0, amount: 0 }],
    total_amount: 0,
    cgst_per: 0,
    sgst_per: 0,
    round_off: 0,
    grand_total: 0,
    quotation: "",
    quotation_no: "",
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (id) {
        try {
          const response = await SalesOrderDetail(id);
          if (!response) return;
          const data = response;
          if (data.project) {
            const fullPath = data.project.split("/").pop();
            const fileName = fullPath.split("?")[0];
            if (fileName.length > 20) {
              const ext = fileName.split(".").pop();
              setExistingFileName(`${fileName.substring(0, 15)}...${ext}`);
            } else {
              setExistingFileName(fileName);
            }
          }

          const mappedItems = (data.items || []).map((item) => ({
            category: {
              id: item.category || item.category?.id,
              name: item.category?.name || item.category_name || "",
            },
            item: {
              id: item.item || item.item?.item || item.item || null,
              name: item.item?.name || item.item_name || "",
              unit: item.unit || item.item?.unit || "",
              rate: item.rate || item.item?.rate || 0,
            },
            description: item.description || "",
            quantity: parseFloat(item.quantity) || 0,
            unit: item.unit || item.item?.unit || "",
            rate: parseFloat(item.rate) || 0,
            amount: parseFloat(item.amount) || 0,
            dis_per: parseFloat(item.dis_per) || 0,
          }));

          mappedItems.forEach(async (row, index) => {
            if (row.category?.id) {
              try {
                const subcatData = await fetchSubCategories(row.category.id);
                setSubcategories((prev) => {
                  const updated = [...prev];
                  updated[index] = subcatData;
                  return updated;
                });
              } catch { }
              try {
                const itemsData = await fetchItemsByCategoryAndSubcategory(row.category?.id, 0);
                const list = (itemsData?.results || itemsData || []).map((item) => ({
                  ...item,
                  id: item.id,
                  name: item.name,
                }));
                setItems((prev) => ({ ...prev, [index]: list }));
              } catch { }
            }
          });

          const parsedTerms = Array.isArray(data.terms_and_conditions)
            ? data.terms_and_conditions
            : JSON.parse(data.terms_and_conditions || "[]");

          setFormData({
            ...initialFormData,
            ...data,
            id: id,
            division: divisionList?.division || data.division || "",
            division_name: divisionList?.division || data.division_name || "",
            items: mappedItems,
            terms_and_conditions: parsedTerms,
            quotation: data.quotation || "",
            quotation_no: data.quotation_no || data.quotation_ref_no || "",
          });

          setTermsAndConditions(parsedTerms);

          if (data.quotation && quotationOptions.length > 0) {
            const match = quotationOptions.find(
              (q) => q.id === data.quotation || q.quotation_id === data.quotation
            );
            if (match) setSelectedQuotation(match);
          }
        } catch (err) {
          toast.error("Failed to load order");
        }
      }
    };
    fetchOrder();
  }, [id, quotationOptions]);

  useEffect(() => {
    if (orderData && itemOptions.length > 0) {
      const normalizedItems = (orderData.items || []).map((item) => {
        const itemId = item.item?.id || item.item;
        const selectedItem = itemOptions.find((opt) => opt.id === itemId);
        return {
          item: item.id,
          category: selectedItem?.category || "",
          description: item.description || "",
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || selectedItem?.unit || "",
          unit_name: selectedItem?.unit_name || "",
          rate: parseFloat(item.rate) || 0,
          amount: parseFloat(item.amount) || 0,
        };
      });

      let safeTerms = [];
      if (Array.isArray(orderData.terms_and_conditions)) {
        safeTerms = orderData.terms_and_conditions;
      } else {
        try {
          const parsed = JSON.parse(orderData.terms_and_conditions);
          if (Array.isArray(parsed)) safeTerms = parsed;
        } catch (e) { }
      }

      setFormData({
        ...initialFormData,
        ...orderData,
        id: id,
        items: normalizedItems,
        terms_and_conditions: safeTerms,
      });
      setTermsAndConditions(safeTerms);
    }
  }, [orderData, itemOptions]);

  const [termsAndConditions, setTermsAndConditions] = useState(formData.terms_and_conditions);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── MANUALLY CHANGING QTY, RATE, DISCOUNT ────────────────────────
  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...formData.items];
    updated[index][name] = value;

    if (name === "item") {
      const selected = itemOptions.find((opt) => String(opt.id) === value);
      updated[index].unit = selected?.unit || "";
      updated[index].unit_name = selected?.unit_name || "";
    }

    const qty = parseFloat(updated[index].quantity) || 0;
    const rate = parseFloat(updated[index].rate) || 0;
    const dis_per = parseFloat(updated[index].dis_per) || 0;
    updated[index].amount = qty * rate - (qty * rate * dis_per) / 100;

    const total_amount = updated.reduce((s, i) => s + Number(i.amount || 0), 0);
    const cgst_amt = (total_amount * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total_amount * (formData.sgst_per || 0)) / 100;
    const grand_total = total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      items: updated,
      total_amount: Number(total_amount.toFixed(2)),
      cgst_amt: Number(cgst_amt.toFixed(2)),
      sgst_amt: Number(sgst_amt.toFixed(2)),
      grand_total: Number(grand_total.toFixed(2)),
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { category: "", item: "", description: "", quantity: 0, unit: "", rate: 0, amount: 0, dis_per: 0 },
      ],
    }));
  };

  const deleteItem = (index) => {
    const items = formData.items.filter((_, i) => i !== index);
    const total_amount = items.reduce((s, i) => s + Number(i.amount || 0), 0);
    setFormData((prev) => ({ ...prev, items, total_amount }));
  };

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...termsAndConditions];
    updatedTerms[index][field] = value;
    setTermsAndConditions(updatedTerms);
  };

  const addTerm = () => {
    setTermsAndConditions([...termsAndConditions, { key: "", value: "", is_default: false }]);
  };

  const handleCloseItemModal = () => setOpenItemModal(false);
  const removeTerm = (index) => {
    setTermsAndConditions(termsAndConditions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append("project_name", formData.project_name);
    formDataToSend.append("party", formData.party);
    formDataToSend.append("division", formData.division);

    if (amd === "AMD") {
      const soNo = formData.so_no.endsWith("-AMD") ? formData.so_no : `${formData.so_no}-AMD`;
      formDataToSend.append("so_no", soNo);
    } else {
      formDataToSend.append("so_no", formData.so_no);
    }

    formDataToSend.append("order_date", formData.order_date);
    formDataToSend.append("total_amount", formData.total_amount);
    formDataToSend.append("created_by", localStorage.getItem("user_id"));
    formDataToSend.append("cgst_per", formData.cgst_per);
    formDataToSend.append("sgst_per", formData.sgst_per);
    formDataToSend.append("round_off", formData.round_off);
    formDataToSend.append("grand_total", formData.grand_total || 0);
    formDataToSend.append("po_number", formData.po_number);

    if (formData.quotation) formDataToSend.append("quotation", formData.quotation);
    if (file && file instanceof File) formDataToSend.append("project", file);

    const filteredTerms = termsAndConditions.filter(
      (term) => term.key.trim() !== "" && term.value.trim() !== ""
    );

    const cleanedItems = formData.items.map((item) => ({
      category: item.category?.id || item.category,
      item: typeof item.item === "object" ? item.item?.id : item.item,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      rate: item.rate,
      dis_per: item.dis_per,
      amount: item.amount,
    }));

    formDataToSend.append("terms_and_conditions", JSON.stringify(filteredTerms));
    formDataToSend.append("items", JSON.stringify(cleanedItems));

    try {
      let result;
      if (id) {
        result = await SalesOrderUpdate(id, formDataToSend);
        toast.success("Sales Order Updated!");
      } else {
        result = await SalesOrderCreate(formDataToSend);
        toast.success("Sales Order Created!");
      }
      if (result) {
        resetForm();
        setTimeout(() => navigate("/dashboard/saleorderlist"), 1000);
      } else {
        toast.error("Something went wrong.");
      }
    } catch (error) {
      toast.error("Failed to submit Sales Order.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    sessionStorage.removeItem("soDraftHeader");
    setTermsAndConditions(formData.terms_and_conditions);
    setFile(null);
    setSelectedQuotation(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setTimeout(() => navigate("/dashboard/saleorderlist"), 1000);
  };

  // ══════════════════════════════════════════════════════════════════
  // AUTO FILL UNIT & RATE ON SELECTING ITEM
  // ══════════════════════════════════════════════════════════════════
  const handleItemAutocompleteChange = async (index, key, value) => {
    const updatedItems = [...formData.items];
    let row = { ...updatedItems[index] };

    switch (key) {
      case "category":
        row = { 
          ...row, 
          category: value, 
          item: null, 
          unit: "", 
          rate: 0, 
          amount: 0 
        };
        if (value) {
          try {
            const itemData = await fetchItemsByCategoryAndSubcategory(value.id, 0, page, searchTerm);
            const list = Array.isArray(itemData?.results) 
              ? itemData.results 
              : Array.isArray(itemData) 
              ? itemData 
              : [];
            
            // Poora Object save kar rahe hain (Sari keys safe rahengi)
            const mappedItems = list.map((item) => ({
              ...item,
              id: item.id,
              name: item.name,
            }));
            setItems((prev) => ({ ...prev, [index]: mappedItems }));
          } catch (err) {
            console.error("Error fetching items:", err);
          }
        } else {
          setItems((prev) => ({ ...prev, [index]: [] }));
        }
        break;

      case "item":
        // Auto fetch matching unit and standard rate
        const autoUnitId = value?.unit?.id || value?.unit || value?.unit_id || "";
        const autoRate = parseFloat(value?.rate || value?.selling_price || value?.price || 0);

        row = {
          ...row,
          item: value,
          unit: autoUnitId,                  // Auto-filled Unit ID
          rate: autoRate,                    // Auto-filled Rate
          description: value?.description || value?.name || row.description || "",
          stock_qty: value?.current_stock || "",
        };

        // Complete instant dynamic calculation
        const qty = parseFloat(row.quantity) || 0;
        const dis_per = parseFloat(row.dis_per) || 0;
        row.amount = qty * autoRate - (qty * autoRate * dis_per) / 100;
        break;
    }

    updatedItems[index] = row;
    const total_amount = updatedItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const cgst_amt = (total_amount * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total_amount * (formData.sgst_per || 0)) / 100;
    const grand_total = total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      total_amount: Number(total_amount.toFixed(2)),
      cgst_amt: Number(cgst_amt.toFixed(2)),
      sgst_amt: Number(sgst_amt.toFixed(2)),
      grand_total: Number(grand_total.toFixed(2)),
    }));
  };

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (!newValue || newValue.trim() === "") {
        if (type === "category") {
          const catData = await getCategories();
          setCategories(Array.isArray(catData?.results) ? catData.results : catData || []);
        }
        if (type === "item" && row.category) {
          try {
            const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, 0);
            const list = Array.isArray(itemData?.results) ? itemData.results : Array.isArray(itemData) ? itemData : [];
            const mappedItems = list.map((i) => ({ ...i, id: i.id, name: i.name }));
            setItems((prev) => ({ ...prev, [index]: mappedItems }));
          } catch { }
        }
        return;
      }
      if (type === "category") {
        const catData = await getCategories(1, newValue || "");
        setCategories(Array.isArray(catData?.results) ? catData.results : catData || []);
      }
      if (type === "item" && row.category) {
        try {
          const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, 0, 1, newValue || "");
          const list = Array.isArray(itemData?.results) ? itemData.results : Array.isArray(itemData) ? itemData : [];
          const mappedItems = list.map((i) => ({ ...i, id: i.id, name: i.name }));
          setItems((prev) => ({ ...prev, [index]: mappedItems }));
        } catch { }
      }
    }, 500),
    []
  );

  const handleSaveDraft = () => {
    sessionStorage.setItem("soDraftHeader", JSON.stringify(formData));
    toast.info("Sales Order draft saved!");
  };

  useEffect(() => {
    const draftHeader = sessionStorage.getItem("soDraftHeader");
    if (draftHeader) {
      try {
        const parsed = JSON.parse(draftHeader);
        setFormData(parsed);
        toast.info("Loaded Sales Order draft");
      } catch (e) { }
    }
  }, []);

  // Common UI styles
  const inputSx = {
    "& .MuiInputBase-root": { height: 40 },
    "& .MuiOutlinedInput-input": { padding: "8px 12px", fontSize: "0.85rem" },
    "& .MuiInputLabel-root": { fontSize: "0.85rem" },
  };

  const sectionTitleSx = {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#333",
    mb: 1.5,
    pb: 0.8,
    borderBottom: "2px solid #1976d2",
    display: "inline-block",
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* ═══════════════════════════════════════════════════════════
            PAGE HEADER & ACTION BUTTONS
        ═══════════════════════════════════════════════════════════ */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            bgcolor: "#fafafa",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1a2e" }}>
              {isEditMode ? "Edit Sales Order" : "New Sales Order"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#666", mt: 0.3 }}>
              Fill in the details below to {isEditMode ? "update" : "create"} the sales order
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => navigate("/dashboard/saleorderlist")}
              size="medium"
              sx={{ textTransform: "none", fontWeight: 600, px: 3, color: "#666", borderColor: "#bbb" }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<DraftsIcon />}
              onClick={handleSaveDraft}
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                color: "#ff8400",
                borderColor: "#ff8400",
                "&:hover": { borderColor: "#e67300", bgcolor: "#fff3e0" },
              }}
            >
              Save Draft
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? null : <SaveIcon />}
              disabled={loading}
              size="small"
              sx={{ textTransform: "none", fontWeight: 600, px: 3, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode ? (
                "Update Order"
              ) : (
                "Submit Order"
              )}
            </Button>
          </Stack>
        </Paper>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 1 : BASIC INFO
        ═══════════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Typography sx={sectionTitleSx}>Order Information</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="SO No." name="so_no" value={formData.so_no || ""} disabled sx={inputSx} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="PO Number" name="po_number" value={formData.po_number} onChange={handleChange} required sx={inputSx} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                name="order_date"
                label="PO Date"
                value={formData.order_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={quotationOptions}
                getOptionLabel={(opt) =>
                  opt ? `${opt.ref_no || ""} — ${opt.party_details?.company_name || opt.to_company || ""}` : ""
                }
                value={selectedQuotation}
                onChange={handleQuotationSelect}
                isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id || option.quotation_id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#1976d2" }}>
                        {option.ref_no}
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
                        — {option.party_details?.company_name || option.to_company || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Link Quotation (Optional)" sx={inputSx} />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(option) => option.company_name || ""}
                value={partyOptions.find((option) => option.id === formData.party) || null}
                onChange={(event, newValue) => setFormData((prev) => ({ ...prev, party: newValue ? newValue.id : "" }))}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") setPartySearch(newInputValue);
                }}
                isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                renderInput={(params) => <TextField {...params} label="Debtor / Party" required sx={inputSx} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Project Name" name="project_name" value={formData.project_name} onChange={handleChange} required sx={inputSx} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="file"
                name="project"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setExistingFileName("");
                }}
                inputRef={fileInputRef}
                InputLabelProps={{ shrink: true }}
                label={existingFileName ? "Change Project File" : "Upload Project File"}
                variant="outlined"
                inputProps={{ accept: "*/*" }}
                sx={inputSx}
              />
              {existingFileName && formData.project && isEditMode && (
                <Box mt={0.5} display="flex" alignItems="center" gap={1} sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                  <span>Current: {existingFileName}</span>
                  <IconButton size="small" onClick={() => window.open(formData.project, "_blank")}>
                    <VisibilityIcon fontSize="small" color="primary" />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 2 : ITEMS (MOBILE & DESKTOP DESIGNS)
        ═══════════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography sx={sectionTitleSx}>Order Items</Typography>
            <Button variant="outlined" onClick={addItem} startIcon={<AddIcon />} size="small" sx={{ textTransform: "none", fontWeight: 600 }}>
              Add Item
            </Button>
          </Box>

          {isMobile ? (
            <Box>
              {formData.items.map((item, index) => (
                <Card key={index} sx={{ mb: 2, border: "1px solid #ddd", boxShadow: "none" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#1976d2" }}>
                        Item #{index + 1}
                      </Typography>
                      <IconButton size="small" onClick={() => deleteItem(index)} sx={{ color: "#d32f2f" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Stack spacing={1.5}>
                      <Autocomplete
                        options={categories}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={item.category || null}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear" || !val) {
                            await handleItemInputChange(index, item, "", "category");
                            handleItemAutocompleteChange(index, "category", null);
                            return;
                          }
                          handleItemAutocompleteChange(index, "category", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") handleItemInputChange(index, item, newValue, "category");
                        }}
                        isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                        renderInput={(params) => <TextField {...params} label="Category" size="small" sx={inputSx} />}
                      />
                      <Autocomplete
                        options={items[index] || []}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={item.item || null}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear" || !val) {
                            await handleItemInputChange(index, item, "", "item");
                            handleItemAutocompleteChange(index, "item", null);
                            return;
                          }
                          handleItemAutocompleteChange(index, "item", val);
                        }}
                        onInputChange={(e, newValue) => handleItemInputChange(index, item, newValue, "item")}
                        isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                        renderInput={(params) => <TextField {...params} label="Item" size="small" sx={inputSx} />}
                      />
                      <TextField fullWidth label="Description" name="description" value={item.description} onChange={(e) => handleItemChange(e, index)} size="small" sx={inputSx} />
                      <Autocomplete
                        options={units}
                        getOptionLabel={(option) => option.name || ""}
                        value={units.find((u) => u.id === item.unit || u.id === item.unit?.id) || null}
                        onChange={(e, newVal) => {
                          const updatedItems = [...formData.items];
                          updatedItems[index].unit = newVal?.id || "";
                          setFormData((prev) => ({ ...prev, items: updatedItems }));
                        }}
                        isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                        renderInput={(params) => <TextField {...params} label="Unit" size="small" sx={inputSx} />}
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <TextField fullWidth label="Qty" type="number" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(e, index)} size="small" sx={inputSx} />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField fullWidth label="Rate" type="number" name="rate" value={item.rate} onChange={(e) => handleItemChange(e, index)} size="small" sx={inputSx} />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField fullWidth label="Disc %" type="number" name="dis_per" value={item.dis_per} onChange={(e) => handleItemChange(e, index)} size="small" sx={inputSx} />
                        </Grid>
                      </Grid>
                      <TextField fullWidth label="Total Amount" value={item.amount} size="small" disabled sx={inputSx} />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Table size="small" sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    {["Category", "Item", "Description", "Unit", "Qty", "Rate", "Disc %", "Total", "Action"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#333", borderBottom: "1px solid #ddd", py: 1.2 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index} sx={{ "&:hover": { bgcolor: "#fafafa" } }}>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(opt) => opt.name || ""}
                          value={item.category || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear" || !val) {
                              await handleItemInputChange(index, item, "", "category");
                              handleItemAutocompleteChange(index, "category", null);
                              return;
                            }
                            handleItemAutocompleteChange(index, "category", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") handleItemInputChange(index, item, newValue, "category");
                          }}
                          isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                          renderInput={(params) => <TextField {...params} placeholder="Category" sx={inputSx} />}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Autocomplete
                          options={items[index] || []}
                          getOptionLabel={(opt) => opt.name || ""}
                          value={item.item || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear" || !val) {
                              await handleItemInputChange(index, item, "", "item");
                              handleItemAutocompleteChange(index, "item", null);
                              return;
                            }
                            handleItemAutocompleteChange(index, "item", val);
                          }}
                          onInputChange={(e, newValue) => handleItemInputChange(index, item, newValue, "item")}
                          isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                          renderInput={(params) => <TextField {...params} placeholder="Item" sx={inputSx} />}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <TextField fullWidth name="description" value={item.description} onChange={(e) => handleItemChange(e, index)} sx={inputSx} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <Autocomplete
                          options={units}
                          getOptionLabel={(option) => option.name || ""}
                          value={units.find((u) => u.id === item.unit || u.id === item.unit?.id) || null}
                          onChange={(e, newVal) => {
                            const updatedItems = [...formData.items];
                            updatedItems[index].unit = newVal?.id || "";
                            setFormData((prev) => ({ ...prev, items: updatedItems }));
                          }}
                          isOptionEqualToValue={(option, val) => option.id === (val?.id || val)}
                          renderInput={(params) => <TextField {...params} placeholder="Unit" sx={inputSx} />}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        <TextField fullWidth type="number" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(e, index)} sx={inputSx} />
                      </TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <TextField fullWidth type="number" name="rate" value={item.rate} onChange={(e) => handleItemChange(e, index)} sx={inputSx} />
                      </TableCell>
                      <TableCell sx={{ width: 90 }}>
                        <TextField fullWidth type="number" name="dis_per" value={item.dis_per} onChange={(e) => handleItemChange(e, index)} sx={inputSx} />
                      </TableCell>
                      <TableCell sx={{ width: 120 }}>
                        <TextField fullWidth value={item.amount} disabled sx={inputSx} />
                      </TableCell>
                      <TableCell sx={{ width: 60 }} align="center">
                        <IconButton onClick={() => deleteItem(index)} size="small" sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 3 : TOTALS
        ═══════════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Typography sx={sectionTitleSx}>Amount Summary</Typography>

          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={8} md={5}>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#555", minWidth: 140 }}>
                    Sub Total
                  </Typography>
                  <TextField value={formData.total_amount} disabled size="small" sx={{ ...inputSx, width: 160 }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#555", minWidth: 140 }}>
                    CGST + SGST (%)
                  </Typography>
                  <TextField
                    type="number" size="small" value={formData.cgst_per || 0}
                    onChange={(e) => {
                      const cgst_per = parseFloat(e.target.value) || 0;
                      const cgst_amt = (formData.total_amount * cgst_per) / 100;
                      const sgst_amt = (formData.total_amount * (formData.sgst_per || 0)) / 100;
                      const grand_total = formData.total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);
                      setFormData((prev) => ({ ...prev, cgst_per, grand_total }));
                    }}
                    sx={{ ...inputSx, width: 160 }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#555", minWidth: 140 }}>
                    IGST (%)
                  </Typography>
                  <TextField
                    type="number" size="small" value={formData.sgst_per || 0}
                    onChange={(e) => {
                      const sgst_per = parseFloat(e.target.value) || 0;
                      const cgst_amt = (formData.total_amount * (formData.cgst_per || 0)) / 100;
                      const sgst_amt = (formData.total_amount * sgst_per) / 100;
                      const grand_total = formData.total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);
                      setFormData((prev) => ({ ...prev, sgst_per, grand_total }));
                    }}
                    sx={{ ...inputSx, width: 160 }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#555", minWidth: 140 }}>
                    Round Off
                  </Typography>
                  <TextField
                    type="number" size="small" value={formData.round_off || 0}
                    onChange={(e) => {
                      const round_off = parseFloat(e.target.value) || 0;
                      const cgst_amt = (formData.total_amount * (formData.cgst_per || 0)) / 100;
                      const sgst_amt = (formData.total_amount * (formData.sgst_per || 0)) / 100;
                      const grand_total = formData.total_amount + cgst_amt + sgst_amt + round_off;
                      setFormData((prev) => ({ ...prev, round_off, grand_total }));
                    }}
                    sx={{ ...inputSx, width: 160 }}
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    bgcolor: "#e8f5e9",
                    p: 1.2,
                    borderRadius: 1,
                    border: "1px solid #a5d6a7",
                  }}
                >
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#2e7d32", minWidth: 140 }}>
                    Grand Total
                  </Typography>
                  <TextField
                    disabled value={formData.grand_total} size="small"
                    sx={{
                      ...inputSx,
                      width: 160,
                      "& .MuiInputBase-input": { fontWeight: 700, color: "#2e7d32" },
                    }}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 4 : TERMS & CONDITIONS
        ═══════════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography sx={sectionTitleSx}>Terms & Conditions</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addTerm} size="small" sx={{ textTransform: "none", fontWeight: 600 }}>
              Add Term
            </Button>
          </Box>

          <Stack spacing={1}>
            {termsAndConditions.map((term, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth size="small" value={term.key}
                    onChange={(e) => handleTermChange(index, "key", e.target.value)}
                    placeholder="Term Name" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={11} sm={8}>
                  <TextField
                    fullWidth size="small" value={term.value}
                    onChange={(e) => handleTermChange(index, "value", e.target.value)}
                    placeholder="Value / Description" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={1} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                  <IconButton onClick={() => removeTerm(index)} size="small" sx={{ color: "#d32f2f" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>
        </Paper>

        {isMobile && <Box sx={{ height: 20 }} />}
      </form>

      <Dialog open={openItemModal} onClose={handleCloseItemModal} fullWidth maxWidth="sm">
        <DialogTitle>Add Item</DialogTitle>
        <DialogContent>
          <AddItemForm onClose={handleCloseItemModal} />
        </DialogContent>
      </Dialog>
    </Container>
  );
}